import { useEffect, useRef } from 'react';
import Lenis from 'lenis';

interface LenisOptions {
  duration?: number;
  easing?: (t: number) => number;
  orientation?: 'vertical' | 'horizontal';
  gestureOrientation?: 'vertical' | 'horizontal' | 'both';
  smoothWheel?: boolean;
  smoothTouch?: boolean;
  wheelMultiplier?: number;
  touchMultiplier?: number;
  infinite?: boolean;
  autoResize?: boolean;
}

export const useLenis = (options: LenisOptions = {}) => {
  const lenisRef = useRef<Lenis | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Initialize Lenis with smooth scrolling options
    const lenis = new Lenis({
      duration: 0.6, // Duration of scroll animation (higher = slower) - reduced for quicker stopping
      easing: (t: number) => 1 - Math.pow(1 - t, 4), // Aggressive cubic ease-out for harsher, quicker stop
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true, // Enable smooth scrolling for mouse wheel
      smoothTouch: false, // Disable on mobile for better performance
      wheelMultiplier: 1, // Mouse wheel sensitivity
      touchMultiplier: 2, // Touch scroll sensitivity
      infinite: false,
      autoResize: true, // Automatically handle window resizing
      ...options,
    });

    lenisRef.current = lenis;

    // Make Lenis globally accessible for components that need it
    (window as { lenis?: Lenis }).lenis = lenis;

    // Animation loop for Lenis
    function raf(time: number) {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    }

    rafRef.current = requestAnimationFrame(raf);

    // Handle page navigation and smooth scrolling to anchors
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.slice(1);
        if (id) {
          const element = document.getElementById(id);
          if (element) {
            lenis.scrollTo(element, {
              duration: 1.5,
              easing: (t: number) => 1 - Math.pow(1 - t, 3), // Ease-out cubic for anchor navigation
            });
          }
        }
      }
    };

    // Add event listener for anchor clicks
    document.addEventListener('click', handleAnchorClick);

    // Cleanup function
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      lenis.destroy();
      document.removeEventListener('click', handleAnchorClick);
    };
  }, [options]);

  // Return Lenis instance for manual control if needed
  return lenisRef.current;
};

export default useLenis;
